import React, { useState, useRef, useCallback, useMemo } from 'react';
import { getAuthenticatedMediaUrl } from '../utils/mediaUtils';
import { LOGO } from '../constants/images';
import { IS_DEVELOPMENT, IS_LOCAL } from '../config';
import { useTutorial } from '../contexts/TutorialContext';
import { studioTutorial, homePageTutorial, promptsPageTutorial, filteringPromptsTutorial, ALL_TUTORIALS } from '../tutorials';
import useClickOutside from '../hooks/useClickOutside';
import '../App.css';
import './Tutorial/Tutorial.css';

// Map tutorial keys to their imported objects
const TUTORIAL_MAP = {
  studioTutorial,
  homePageTutorial,
  promptsPageTutorial,
  filteringPromptsTutorial
};

function Header({ user, showProfileMenu, setShowProfileMenu, onSettingsClick, onAdminClick, onLogout, isAdmin, onTitleClick, onMarkAllViewed, onMarkAllUnviewed, onExportClick, onNavigateToPrompts, onNavigateToHome, stickyHeader }) {
  const profileMenuRef = useRef(null);
  const helpMenuRef = useRef(null);
  const [showHelpMenu, setShowHelpMenu] = useState(false);
  const { startTutorial } = useTutorial();

  // Get authenticated headshot URL
  const authenticatedHeadshotUrl = useMemo(() =>
    getAuthenticatedMediaUrl(user?.headshotUrl),
    [user?.headshotUrl]
  );

  const closeProfileMenu = useCallback(() => setShowProfileMenu(false), [setShowProfileMenu]);
  const closeHelpMenu = useCallback(() => setShowHelpMenu(false), []);
  useClickOutside(profileMenuRef, closeProfileMenu);
  useClickOutside(helpMenuRef, closeHelpMenu, showHelpMenu);

  const handleStartTutorial = useCallback((entry) => {
    setShowHelpMenu(false);
    if (entry.page === 'home' && onNavigateToHome) {
      onNavigateToHome();
    } else if (onNavigateToPrompts) {
      onNavigateToPrompts();
    }
    const tutorial = TUTORIAL_MAP[entry.tutorialKey];
    if (tutorial) startTutorial(tutorial);
  }, [onNavigateToHome, onNavigateToPrompts, startTutorial]);

  return (
    <header className={`header${stickyHeader ? ' header--sticky' : ''}`}>
      <div className="header-content">
        <div
          onClick={onTitleClick}
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: '1rem',
            cursor: onTitleClick ? 'pointer' : 'default'
          }}
        >
          <img
            className="header-logo"
            src={LOGO}  // Using constant from constants/images.js
            alt="Brings to Mind Logo"
            style={{
              height: '60px',
              width: 'auto'
            }}
          />
          <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start' }}>
            <h1 style={{ margin: 0, textAlign: 'left' }}>
              BRINGS TO MIND{IS_DEVELOPMENT ? ' - Development' : IS_LOCAL ? ' - Local Dev' : ''}
            </h1>
            <p style={{ margin: 0, textAlign: 'left' }}>REFLECTIONS ON ALL LIFE'S MOMENTS</p>
          </div>
        </div>
        <div className="user-info">
          <div className="help-menu-container" ref={helpMenuRef}>
            <button
              className="help-btn"
              onClick={() => setShowHelpMenu(!showHelpMenu)}
              title="Help & Tutorials"
            >
              ?
            </button>
            {showHelpMenu && (
              <div className="help-dropdown">
                {ALL_TUTORIALS.map(entry => (
                  <button
                    key={entry.id}
                    className="help-dropdown-item"
                    onClick={() => handleStartTutorial(entry)}
                  >
                    {entry.label}
                  </button>
                ))}
              </div>
            )}
          </div>
          <span>{user?.username}</span>
          <div className="profile-menu-container" ref={profileMenuRef}>
            <button
              className="profile-avatar-button"
              onClick={() => setShowProfileMenu(!showProfileMenu)}
              style={authenticatedHeadshotUrl ? {
                padding: 0,
                overflow: 'hidden',
                background: 'transparent'
              } : {}}
            >
              {authenticatedHeadshotUrl ? (
                <img
                  src={authenticatedHeadshotUrl}
                  alt="Profile"
                  style={{
                    width: '100%',
                    height: '100%',
                    objectFit: 'cover'
                  }}
                />
              ) : (
                user?.username?.charAt(0).toUpperCase() || 'U'
              )}
            </button>
            {showProfileMenu && (
              <div className="profile-dropdown">
                <button onClick={() => { onSettingsClick(); setShowProfileMenu(false); }}>
                  Settings
                </button>
                {isAdmin && (
                  <button onClick={() => { onAdminClick(); setShowProfileMenu(false); }}>
                    Admin
                  </button>
                )}
                <button onClick={() => { onMarkAllViewed(); setShowProfileMenu(false); }}>
                  Mark All Viewed
                </button>
                <button onClick={() => { onMarkAllUnviewed(); setShowProfileMenu(false); }}>
                  Mark All Unviewed
                </button>
                {onExportClick && (
                  <button onClick={() => { onExportClick(); setShowProfileMenu(false); }}>
                    Export Reflections
                  </button>
                )}
                <button onClick={() => { setShowProfileMenu(false); onLogout(); }}>
                  Logout
                </button>
              </div>
            )}
          </div>
        </div>
      </div>
    </header>
  );
}

export default Header;
